import { useState, useRef, useEffect } from 'react';
import { useAppStore } from '../lib/store';
import type { WorkType } from '../lib/store';
import { WORK_TYPE_OPTIONS } from '../lib/store';

// ─── Constants ────────────────────────────────────────────────────────────────

const COVER_COLORS = ['#5A7A7A', '#6B8E7A', '#7A8B9A', '#8A7A9A', '#7A9A8A', '#8A8A8A', '#9A7A7A', '#7A7A9A'];

const WORD_COUNT_PRESETS = [
    { label: 'Short story', value: 7500 },
    { label: 'Novella', value: 40000 },
    { label: 'Novel', value: 80000 },
    { label: 'Epic', value: 150000 },
];

export interface NovelData {
    id: string;
    title: string;
    description: string;
    workType?: WorkType;
    coverColor: string;
    targetWordCount: number;
}

interface CreateNovelFormProps {
    /** If provided, the form is in edit mode */
    novel?: NovelData;
    onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CreateNovelForm({ novel, onClose }: CreateNovelFormProps) {
    const isEdit = !!novel;

    const [title, setTitle] = useState(novel?.title ?? '');
    const [description, setDescription] = useState(novel?.description ?? '');
    const [workType, setWorkType] = useState<WorkType>(novel?.workType ?? 'fiction');
    const [coverColor, setCoverColor] = useState(novel?.coverColor ?? COVER_COLORS[0]);
    const [targetWordCount, setTargetWordCount] = useState(novel?.targetWordCount ?? 80000);
    const [wordCountInput, setWordCountInput] = useState(String(novel?.targetWordCount ?? 80000));
    const [saving, setSaving] = useState(false);

    const titleRef = useRef<HTMLInputElement>(null);

    // Store connections
    const createNovel = useAppStore(s => s.createNovel);
    const updateNovel = useAppStore(s => s.updateNovel);
    const setActiveNovel = useAppStore(s => s.setActiveNovel);
    const setViewMode = useAppStore(s => s.setViewMode);

    useEffect(() => {
        setTimeout(() => titleRef.current?.focus(), 80);
    }, []);

    const handleWordCountChange = (val: string) => {
        setWordCountInput(val);
        const n = parseInt(val.replace(/,/g, ''), 10);
        if (!isNaN(n) && n > 0) setTargetWordCount(n);
    };

    const handleSubmit = () => {
        if (!title.trim()) return;
        setSaving(true);

        if (isEdit && novel) {
            updateNovel(novel.id, { title: title.trim(), description, workType, coverColor, targetWordCount });
        } else {
            const id = createNovel(title.trim(), description, workType);
            updateNovel(id, { coverColor, targetWordCount });
            setActiveNovel(id);
            setViewMode('manuscript');
        }

        // Slight artificial delay to feel processed
        setTimeout(() => { setSaving(false); onClose(); }, 600);
    };

    // Get accent color from current theme (falls back to default)
    const accent = 'var(--studio-accent, #8B7355)';
    const bg = 'var(--studio-bg, #F5F1E8)';
    const paper = 'var(--studio-paper, #FAF7F0)';
    const text = 'var(--studio-text, #2C2416)';
    const border = 'var(--studio-border, rgba(44,36,22,0.12))';

    const canSave = title.trim().length > 0;

    return (
        <>
            <style>{STYLES}</style>

            {/* Overlay */}
            <div className="cnf-overlay" onClick={onClose} />

            {/* Sheet */}
            <div className="cnf-sheet" style={{ background: paper, border: `1px solid ${border}`, color: text }}>

                {/* Header */}
                <div className="cnf-header">
                    <h2 className="cnf-heading" style={{ color: text }}>
                        {isEdit ? 'Edit project' : 'New project'}
                    </h2>
                    <button className="cnf-close" style={{ color: text, borderColor: border }} onClick={onClose} aria-label="Close">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                            <path d="M1 1l12 12M13 1L1 13" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="cnf-body">

                    {/* Work type */}
                    <div className="cnf-field">
                        <label className="cnf-label" style={{ color: text }}>Type of work</label>
                        <div className="cnf-type-grid">
                            {WORK_TYPE_OPTIONS.map(w => (
                                <button
                                    key={w.id}
                                    className={`cnf-type-btn \${workType === w.id ? 'cnf-type-btn--active' : ''}`}
                                    onClick={() => setWorkType(w.id)}
                                    style={{
                                        borderColor: workType === w.id ? accent : border,
                                        background: workType === w.id ? `color-mix(in srgb, var(--studio-accent, #8B7355) 10%, transparent)` : 'transparent',
                                        color: text,
                                    }}
                                    title={w.description}
                                >
                                    <span className="cnf-type-glyph" style={{ color: workType === w.id ? accent : text }}>{w.glyph}</span>
                                    <span className="cnf-type-label">{w.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Title */}
                    <div className="cnf-field">
                        <label className="cnf-label" style={{ color: text }}>Title</label>
                        <input
                            ref={titleRef}
                            className="cnf-input cnf-input--title"
                            type="text"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && canSave) handleSubmit(); }}
                            placeholder={
                                workType === 'fiction' ? 'The name of your novel…' :
                                    workType === 'nonfiction' ? 'Working title…' :
                                        workType === 'poetry' ? 'Name of this collection…' :
                                            workType === 'screenwriting' ? 'Project title…' :
                                                'My Journal'
                            }
                            maxLength={120}
                            autoComplete="off"
                            style={{ background: bg, borderColor: title ? accent : border, color: text, boxShadow: title ? `0 0 0 3px color-mix(in srgb, var(--studio-accent, #8B7355) 12%, transparent)` : 'none' }}
                        />
                    </div>

                    {/* Description */}
                    <div className="cnf-field">
                        <label className="cnf-label" style={{ color: text }}>
                            Description
                            <span className="cnf-optional">optional</span>
                        </label>
                        <textarea
                            className="cnf-textarea"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="A brief premise or note to yourself…"
                            rows={3}
                            maxLength={500}
                            style={{ background: bg, borderColor: border, color: text }}
                        />
                    </div>

                    {/* Cover color */}
                    <div className="cnf-field">
                        <label className="cnf-label" style={{ color: text }}>Cover colour</label>
                        <div className="cnf-color-row">
                            {COVER_COLORS.map(c => (
                                <button
                                    key={c}
                                    className={`cnf-color-swatch \${coverColor === c ? 'cnf-color-swatch--active' : ''}`}
                                    onClick={() => setCoverColor(c)}
                                    style={{ background: c, boxShadow: coverColor === c ? `0 0 0 2px \${paper}, 0 0 0 4px \${c}` : 'none' }}
                                    aria-label={`Cover colour \${c}`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Target word count */}
                    <div className="cnf-field">
                        <label className="cnf-label" style={{ color: text }}>Target word count</label>
                        <div className="cnf-wordcount-row">
                            {WORD_COUNT_PRESETS.map(p => (
                                <button
                                    key={p.value}
                                    className={`cnf-preset-btn \${targetWordCount === p.value ? 'cnf-preset-btn--active' : ''}`}
                                    onClick={() => { setTargetWordCount(p.value); setWordCountInput(String(p.value)); }}
                                    style={{
                                        borderColor: targetWordCount === p.value ? accent : border,
                                        color: targetWordCount === p.value ? accent : text,
                                        background: 'transparent',
                                    }}
                                >
                                    <span className="cnf-preset-label">{p.label}</span>
                                    <span className="cnf-preset-count">{p.value.toLocaleString()}</span>
                                </button>
                            ))}
                        </div>
                        <input
                            className="cnf-input cnf-input--wordcount"
                            type="text"
                            inputMode="numeric"
                            value={wordCountInput}
                            onChange={e => handleWordCountChange(e.target.value)}
                            placeholder="Custom…"
                            style={{ background: bg, borderColor: border, color: text, marginTop: 10 }}
                        />
                    </div>

                </div>

                {/* Footer */}
                <div className="cnf-footer" style={{ borderColor: border }}>
                    <button className="cnf-btn cnf-btn--ghost" style={{ color: text, borderColor: border }} onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className={`cnf-btn cnf-btn--primary ${!canSave ? 'cnf-btn--disabled' : ''}`}
                        style={{ background: accent }}
                        onClick={handleSubmit}
                        disabled={!canSave || saving}
                    >
                        {saving ? (
                            <span className="cnf-spinner" />
                        ) : (
                            isEdit ? 'Save changes' : 'Create project'
                        )}
                    </button>
                </div>

            </div>
        </>
    );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const STYLES = `
  .cnf-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.4);
    backdrop-filter: blur(4px);
    z-index: var(--z-modal);
    animation: cnfFadeIn 0.2s ease;
  }

  .cnf-sheet {
    position: fixed;
    right: 0; top: 0; bottom: 0;
    width: min(28.75rem, 100vw);
    z-index: calc(var(--z-modal) + 1);
    display: flex; flex-direction: column;
    border-left-width: 1px;
    border-left-style: solid;
    border-radius: 0;
    box-shadow: -0.5rem 0 3rem rgba(0,0,0,0.12);
    animation: cnfSlideIn 0.28s cubic-bezier(0.34,1.2,0.64,1);
    overflow: hidden;
  }
  
  @media (prefers-reduced-motion: reduce) {
    .cnf-overlay, .cnf-sheet {
      animation: none !important;
    }
  }

  .cnf-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 1.5rem 1.75rem 1.25rem;
    flex-shrink: 0;
  }

  .cnf-heading {
    font-family: 'Crimson Text', 'Lora', Georgia, serif;
    font-size: 1.375rem; font-weight: 500; letter-spacing: -0.01em;
  }

  .cnf-close {
    width: 2.5rem; height: 2.5rem; border-radius: 0.5rem;
    border: 1px solid; background: transparent;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; opacity: 0.7; transition: opacity 0.15s;
    min-width: 2.5rem;
    min-height: 2.5rem;
  }
  .cnf-close:hover { opacity: 1; }
  .cnf-close:focus-visible {
    outline: 2px solid var(--sage);
    outline-offset: 2px;
  }

  .cnf-body {
    flex: 1; overflow-y: auto; padding: 0.25rem 1.75rem 1.75rem;
    display: flex; flex-direction: column; gap: 0;
  }

  .cnf-field { margin-bottom: 1.5rem; }

  .cnf-label {
    display: flex; align-items: center; gap: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px; font-weight: 500; letter-spacing: 0.08em;
    text-transform: uppercase; opacity: 0.45; margin-bottom: 8px;
  }

  .cnf-optional {
    font-size: 0.625rem; opacity: 0.6; text-transform: none; letter-spacing: 0; font-weight: 400;
  }

  /* Type grid */
  .cnf-type-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem;
  }

  .cnf-type-btn {
    display: flex; flex-direction: column; align-items: flex-start; gap: 0.1875rem;
    padding: 0.75rem 0.75rem 0.625rem; border-radius: 0.625rem; border: 1px solid;
    cursor: pointer; transition: all 0.16s ease; text-align: left;
  }
  .cnf-type-btn:hover { transform: translateY(-1px); }

  /* Last item full width if it falls alone */
  .cnf-type-grid > :nth-child(4):last-child,
  .cnf-type-grid > :nth-child(5):last-child {
    grid-column: span 1;
  }
  .cnf-type-grid > :nth-child(5) {
    grid-column: 2;
  }

  .cnf-type-glyph { font-size: 0.8125rem; margin-bottom: 0.1875rem; }
  .cnf-type-label { font-family: 'DM Sans', sans-serif; font-size: 0.8125rem; font-weight: 500; }

  /* Inputs */
  .cnf-input {
    width: 100%; padding: 12px 14px; border-radius: 10px; border: 1px solid;
    font-family: 'Crimson Text', 'Lora', Georgia, serif;
    outline: none; transition: border-color 0.18s, box-shadow 0.18s;
    display: block;
  }
  .cnf-input--title { font-size: 1.125rem; }
  .cnf-input--wordcount { font-size: 0.875rem; font-family: 'DM Sans', sans-serif; }
  .cnf-input:focus { border-color: var(--studio-accent, #8B7355); box-shadow: 0 0 0 3px rgba(139,115,85,0.1); }
  .cnf-input::placeholder { opacity: 0.28; font-style: italic; }

  .cnf-textarea {
    width: 100%; padding: 0.75rem 0.875rem; border-radius: 0.625rem; border: 1px solid;
    font-family: 'DM Sans', sans-serif; font-size: 0.875rem; line-height: 1.6;
    outline: none; resize: vertical; min-height: 5rem;
    transition: border-color 0.18s; display: block;
  }
  .cnf-textarea:focus { border-color: var(--studio-accent, #8B7355); }
  .cnf-textarea::placeholder { opacity: 0.28; }

  /* Cover colors */
  .cnf-color-row { display: flex; gap: 8px; flex-wrap: wrap; }
  .cnf-color-swatch {
    width: 28px; height: 28px; border-radius: 50%; border: none;
    cursor: pointer; transition: transform 0.15s, box-shadow 0.15s;
  }
  .cnf-color-swatch:hover { transform: scale(1.15); }
  .cnf-color-swatch--active { transform: scale(1.15); }

  /* Word count presets */
  .cnf-wordcount-row { display: grid; grid-template-columns: repeat(4,1fr); gap: 8px; }
  .cnf-preset-btn {
    display: flex; flex-direction: column; align-items: center; gap: 2px;
    padding: 9px 6px; border-radius: 8px; border: 1px solid;
    cursor: pointer; transition: all 0.15s ease;
  }
  .cnf-preset-btn:hover { transform: translateY(-1px); }
  .cnf-preset-label { font-family: 'DM Sans', sans-serif; font-size: 0.6875rem; font-weight: 500; }
  .cnf-preset-count { font-family: 'DM Sans', sans-serif; font-size: 0.625rem; opacity: 0.6; }

  /* Footer */
  .cnf-footer {
    display: flex; gap: 0.625rem; justify-content: flex-end;
    padding: 1.125rem 1.75rem;
    border-top: 1px solid;
    flex-shrink: 0;
  }

  .cnf-btn {
    padding: 0.625rem 1.25rem; border-radius: 0.5625rem; font-family: 'DM Sans', sans-serif;
    font-size: 0.875rem; font-weight: 500; cursor: pointer; border: none;
    transition: all 0.16s ease; display: flex; align-items: center; gap: 0.375rem;
  }
  .cnf-btn--ghost {
    background: transparent; border: 1px solid;
    opacity: 0.8;
  }
  .cnf-btn--ghost:hover { opacity: 1; }
  .cnf-btn--ghost:focus-visible {
    outline: 2px solid var(--sage);
    outline-offset: 2px;
  }
  .cnf-btn--primary { color: #FAF7F0; box-shadow: 0 2px 8px rgba(0,0,0,0.12); }
  .cnf-btn--primary:hover { filter: brightness(1.08); transform: translateY(-1px); }
  .cnf-btn--primary:active { transform: none; }
  .cnf-btn--disabled { opacity: 0.5 !important; cursor: not-allowed; transform: none !important; filter: none !important; }

  .cnf-spinner {
    width: 0.875rem; height: 0.875rem; border: 2px solid rgba(255,255,255,0.25);
    border-top-color: #FAF7F0; border-radius: 50%;
    animation: cnfSpin 0.7s linear infinite; display: inline-block;
  }

  @keyframes cnfFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes cnfSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
  @keyframes cnfSpin { to { transform: rotate(360deg); } }

  @media (max-width: 480px) {
    .cnf-type-grid { grid-template-columns: repeat(2, 1fr); }
    .cnf-type-grid > :nth-child(5) { grid-column: auto; }
    .cnf-wordcount-row { grid-template-columns: repeat(2,1fr); }
  }
`;
